import { TooltipGroup, TooltipGroupAnchor, useTooltipGroupContext } from '@/components/shared/tooltip/TooltipGroup';
import cx from 'classnames';
import { omit } from 'lodash-es';
import { forwardRef, useId, useMemo } from 'react';
import styles from './Button.module.css';

export const BUTTON_SIZE = {
  L: 'L',
  M: 'M',
  S: 'S',
  XS: 'XS',
} as const;

export type ButtonStandardSize = 'L' | 'M' | 'S';
export type ButtonTextSize = ButtonStandardSize | 'XS';
export type ButtonVariant = 'primary' | 'outlined' | 'text' | 'pending';

export interface ButtonCommonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  color?: 'default' | 'warning';
  loading?: boolean;
  className?: string;
  truncate?: boolean;
}

interface StandardButtonProps extends ButtonCommonProps {
  iconOnly?: false;
  children: React.ReactNode;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

interface IconButtonProps extends ButtonCommonProps {
  iconOnly: true;
  icon: React.ReactNode;
  children?: never;
}

type SizeVariantProps = { size?: ButtonStandardSize; variant?: ButtonVariant } | { size: 'XS'; variant: 'text' };

const isIconOnly = (value: ButtonProps): value is IconButtonProps & SizeVariantProps => {
  return value.iconOnly === true && !!value.icon;
};

export type ButtonProps = (StandardButtonProps | IconButtonProps) & SizeVariantProps;

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    variant = 'primary',
    size = 'L',
    color = 'default',
    loading = false,
    type = 'button',
    className,
    iconOnly,
    disabled,
    truncate = false,
    ...rest
  } = props;

  const tooltipId = useId();
  const isDisabled = disabled || loading;
  const existingTooltipGroup = useTooltipGroupContext();

  const buttonProps = {
    ref,
    type,
    'className': cx(styles.button, className),
    'disabled': isDisabled,
    'data-variant': variant,
    'data-size': size,
    'data-color': color,
    'data-loading': loading,
    'data-icon-only': iconOnly,
    'data-truncate': truncate,
    'aria-busy': loading,
  };

  const content = useMemo(() => {
    const getChildren = () => {
      if (!isIconOnly(props) && truncate && typeof props.children === 'string') {
        return (
          <span className={styles.truncatedText}>
            <TooltipGroupAnchor value={props.children} isTruncatedOptions={{ ignoreWindowResize: true }}>
              {props.children}
            </TooltipGroupAnchor>
          </span>
        );
      }
      return isIconOnly(props) ? null : props.children;
    };

    if (isIconOnly(props)) {
      return loading ? (
        <img src="/gifs/spinner.gif" alt="Loading..." />
      ) : (
        <span className={styles.iconWrapper}>{props.icon}</span>
      );
    }

    const showNotificationDot = variant === 'pending' && !loading;

    return (
      <>
        {loading && <img src="/gifs/spinner.gif" alt="Loading..." />}
        {!loading && showNotificationDot && <span className={styles.notificationDot} aria-hidden="true" />}
        {!loading && props.startIcon && <span className={styles.iconWrapper}>{props.startIcon}</span>}
        {getChildren()}
        {!loading && props.endIcon && <span className={styles.iconWrapper}>{props.endIcon}</span>}
      </>
    );
  }, [loading, props, truncate, variant]);

  const buttonElement = (
    <button {...buttonProps} {...omit(rest, ['startIcon', 'endIcon', 'icon', 'truncate'])}>
      <div className={styles.content}>{content}</div>
    </button>
  );

  if (truncate && !existingTooltipGroup.hasProvider) {
    return <TooltipGroup id={`button-truncate-${tooltipId}`}>{buttonElement}</TooltipGroup>;
  }

  return buttonElement;
});

Button.displayName = 'Button';
export default Button;
